/** A stroke point; `p` is pen pressure (1 when absent). */
export type SketchPoint = { x: number; y: number; p?: number };

function distance(a: SketchPoint, b: SketchPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Interpolates position, and pressure only when either point carries one. */
function mixPoints(a: SketchPoint, b: SketchPoint, t: number): SketchPoint {
  const point: SketchPoint = {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
  if (a.p !== undefined || b.p !== undefined) {
    const from = a.p ?? 1;
    point.p = from + ((b.p ?? 1) - from) * t;
  }
  return point;
}

/** Chaikin corner cutting: rounds corners and jitter while keeping both
 * endpoints. */
export function chaikin(
  points: readonly SketchPoint[],
  iterations: number,
): SketchPoint[] {
  let current = points.slice();
  if (current.length < 2) return current;
  for (let pass = 0; pass < iterations; pass += 1) {
    const next = [current[0]!];
    for (let index = 0; index < current.length - 1; index += 1) {
      const start = current[index]!;
      const end = current[index + 1]!;
      next.push(mixPoints(start, end, 0.25), mixPoints(start, end, 0.75));
    }
    next.push(current[current.length - 1]!);
    current = next;
  }
  return current;
}

/** Even arc-length spacing, so draw-on speed follows stroke length rather than
 * point density. Keeps both endpoints. */
export function resampleByArcLength(
  points: readonly SketchPoint[],
  step: number,
): SketchPoint[] {
  if (points.length < 2) return points.slice();
  const resampled = [points[0]!];
  let carried = 0;
  for (let index = 1; index < points.length; index += 1) {
    let from = points[index - 1]!;
    const to = points[index]!;
    let remaining = distance(from, to);
    while (remaining > 0 && carried + remaining >= step) {
      from = mixPoints(from, to, (step - carried) / remaining);
      resampled.push(from);
      remaining = distance(from, to);
      carried = 0;
    }
    carried += remaining;
  }
  const end = points[points.length - 1]!;
  const last = resampled.length - 1;
  if (last > 0 && distance(resampled[last]!, end) < 1e-6) {
    resampled[last] = end;
  } else {
    resampled.push(end);
  }
  return resampled;
}

export function strokeLength(points: readonly SketchPoint[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += distance(points[index - 1]!, points[index]!);
  }
  return total;
}

/** The part of the stroke drawn at `progress` (0–1) of its arc length. At 0 it
 * is the first point twice; the cut point interpolates position and pressure. */
export function strokePrefix(
  points: readonly SketchPoint[],
  progress: number,
): SketchPoint[] {
  if (points.length < 2) return points.slice();
  const target = strokeLength(points) * Math.max(0, Math.min(1, progress));
  const first = points[0]!;
  if (target <= 0) return [first, first];
  let traveled = 0;
  const prefix = [first];
  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1]!;
    const end = points[index]!;
    const segment = distance(start, end);
    if (traveled + segment <= target) {
      prefix.push(end);
      traveled += segment;
      continue;
    }
    const ratio = segment === 0 ? 1 : (target - traveled) / segment;
    prefix.push(mixPoints(start, end, ratio));
    break;
  }
  return prefix.length >= 2 ? prefix : [first, first];
}
